import { createContext, type PropsWithChildren, useContext } from 'react';

export type NavigationOptions = {
  // Query parameters to include in the URL.
  query?: Record<string, string>;
  // Exclude the site ID from the URL.
  excludeSiteId?: boolean;
  // Exclude the site environment from the URL.
  excludeSiteEnv?: boolean;
  // Open in the SparkLayer Dashboard rather than the current app.
  openInDashboard?: boolean;
  // Replace the URL without saving it in navigation history.
  replace?: boolean;
  // Open the link in a new tab.
  newTab?: boolean;
  // Navigate the top-level window (if reachable) rather than the current frame.
  top?: boolean;
  // Treat `path` as an absolute URL.
  absolute?: boolean;
};

export type NavigateFn = (path: string, options?: NavigationOptions) => void;

/**
 * The only navigation a primitive is allowed to know about.
 *
 * Primitives live below the page providers, so they cannot reach the full
 * `NavigationHandlerContract`. Whoever owns that contract populates this instead.
 */
const NavigationContext = createContext<NavigateFn | undefined>(undefined);

export function NavigationProvider({
  navigate,
  children,
}: PropsWithChildren<{ navigate: NavigateFn }>) {
  return <NavigationContext.Provider value={navigate}>{children}</NavigationContext.Provider>;
}

export function useNavigate(): NavigateFn {
  const navigate = useContext(NavigationContext);
  if (!navigate) {
    throw new Error('useNavigate must be used within a NavigationProvider');
  }
  return navigate;
}
